import React, {useState, useEffect} from 'react'
import ContactUsHeader from './partials/contact-us-header'
import {Box, Button, Flex, Icon, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import EmailIcon from '../../components/icons/support/EmailIcon'
import Field from '../../components/field'
import {useForm} from 'react-hook-form'
import useContactUsFields from './partials/forms/use-contactUs-fields'
import ContactUsFields from './partials/forms/contact-us-fields'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {generateGUID, getValidToken} from '../../utils/utils'
import {BRAND_SHORT_LABEL} from '../../brand'
import {useLocaleCountry, useLocaleLanguage} from '../../hooks/use-locale-custom'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import routingObject from '../../utils/static-routes'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'
import SanitizedHtml from '../../components/html-sanitizer'

const ContactUs = () => {
    const styles = useMultiStyleConfig('ContactUs')
    const form = useForm({mode: 'all', shouldUnregister: false})
    const {addLayer} = useGoogleTagManager()
    const fields = useContactUsFields({form, prefix: ''})
    const navigate = useNavigation()
    const [receiptBase64, setReceiptBase64] = useState('')
    const [receiptName, setReceiptName] = useState('')
    const [receiptMime, setReceiptMime] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('') // Add error message state
    const appOrigin = useOrigin(false)
    const language = useLocaleLanguage()
    const country = useLocaleCountry()
    const {data: customer} = useCurrentCustomer()
    const contactUsConfiguration = useCustomPreferenceWithLocale('contactUsConfiguration') || {}
    const {data: contentstackData} = useGetPageByType('contact_us')

    // eslint-disable-next-line no-console
    console.log('[contact-us] form.requestType', form.watch('requestType'))

    const onSubmit = async (data) => {
        setIsLoading(true)
        setErrorMessage('') // Reset error message before submit
        try {
            const dataWithFile = {
                ...data,
                messageID: generateGUID(),
                brand: BRAND_SHORT_LABEL,
                language: language.toUpperCase(),
                country,
                gigyaId: customer?.c_gigyaId,
                receiptBase64,
                receiptName,
                receiptMime,
            }
            const apiUrl = `${appOrigin}${MRT_API_BASEPATH}/get-auth-token-sap-cpi`
            const accessTokenKey = 'accessTokenSAPCPI'
            const tokenExpirationKey = 'tokenExpirationSAPCPI'

            const accessToken = await getValidToken(apiUrl, accessTokenKey, tokenExpirationKey)
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/push-support-case-sap-cpi`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(dataWithFile),
                },
            )
            const result = await response.json()
            if (result.success) {
                navigateOnSuccess(dataWithFile)
                await addLayer(
                    basic_gtm_event.bind(this, {
                        event: 'contactUs',
                        eventCategory: 'loyalty',
                        eventAction: 'contactUs',
                        eventLabel: 'sent',
                    }),
                )
            } else if (result.error) {
                setErrorMessage(result.error) // Set error message from API
            }
        } catch (error) {
            console.error('Error:', error)
            setErrorMessage(error?.message || 'Unknown error occurred') // Set error message from catch
        } finally {
            setIsLoading(false)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const navigateOnSuccess = (data) => {
        navigate(`${routingObject.CONTACT_US_PATH}/confirmation`)
    }

    const requestType = form.watch('requestType')

    useEffect(() => {
        if (requestType) {
            // Handle logic based on requestType, e.g., filter the fields for that requestType
            const relevantFields = Object.keys(fields).filter((fieldName) => {
                // Filter fields dynamically based on requestType
                return fields[fieldName]?.requestType === requestType && fields[fieldName]?.required
            })

            // Trigger validation only for relevant fields
            relevantFields.forEach((fieldName) => {
                form.trigger(fieldName)
            })
        }
    }, [requestType, fields, form])

    return (
        <Flex {...styles.container} data-gtm="contact-us-page" layerStyle={'page'}>
            <ContactUsHeader />
            <SanitizedHtml as="div" html={contentstackData?.top_section} />
            <Flex {...styles.mainContainer}>
                {contactUsConfiguration && Object.keys(contactUsConfiguration).length !== 0 && (
                    <Flex {...styles.messageContainer}>
                        <Flex {...styles.messageInnerContainer}>
                            <Box {...styles.messageIconTitle}>
                                <Icon as={EmailIcon} {...styles.emailIcon} />
                                <Text {...styles.messageTitle}>
                                    <FormattedMessage
                                        id="contact_us.message_title"
                                        defaultMessage="Send us a message"
                                    />
                                </Text>
                            </Box>
                            <Text {...styles.instructionsText}>
                                <FormattedMessage
                                    id="contact_us.instructions"
                                    defaultMessage="Fill out the contact form to send us your message. We will get back to you shortly."
                                />
                            </Text>
                        </Flex>
                        <Field {...fields.requestType} />
                        {form.watch('requestType') && (
                            <>
                                <ContactUsFields
                                    form={form}
                                    setReceiptBase64={setReceiptBase64}
                                    setReceiptName={setReceiptName}
                                    setReceiptMime={setReceiptMime}
                                />
                                {/* Conditionally render the button */}
                                {Object.keys(fields).length > 1 &&
                                    !(Object.keys(fields).length === 2 && fields.description) && (
                                        <Button
                                            {...styles.button}
                                            variant={'primary'}
                                            onClick={form.handleSubmit(onSubmit)}
                                            isLoading={isLoading}
                                            isDisabled={!form.formState.isValid} // Disable button if form is invalid
                                        >
                                            <Text {...styles.buttonText}>
                                                <FormattedMessage
                                                    id="product_registration_form.complete_registration"
                                                    defaultMessage="Complete the registration"
                                                />
                                            </Text>
                                        </Button>
                                    )}
                            </>
                        )}
                        {errorMessage && (
                            <Box color="red.500" mb={4}>
                                {errorMessage}
                            </Box>
                        )}
                    </Flex>
                )}
                <SanitizedHtml as="div" html={contentstackData?.right_section} />
            </Flex>
            <SanitizedHtml as="div" html={contentstackData?.bottom_section} />
            <Box width="100%">
                {contentstackData?.editorial_components?.map((component) => (
                    <EditorialComponentsFactory
                        key={component.uid}
                        componentType={component?._content_type_uid}
                        {...component}
                    />
                ))}
            </Box>
            {isLoading && <LoadingSpinner />}
        </Flex>
    )
}

export default ContactUs
